// src/drpsmanager2.js
import React, { useState, useEffect, useRef } from 'react';
import { useUser, useAuth, useClerk } from '@clerk/clerk-react';
import { useTenant } from './TenantProvider';
import CreateIncidentModal from './components/CreateIncidentModal';
import CompanySelector from './components/CompanySelector';
import CompanyDebugBar from './components/CompanyDebugBar';
import AddCompanyModal from './components/AddCompanyModal';
import AddUserForm from './components/AddUserForm';
import AddImpersonatingDomainModal from './components/AddImpersonatingDomainModal';
import SocialMediaView from './components/SocialMediaView';
import AddSocialMediaModal from './components/AddSocialMediaModal';
import DarkWebView from './components/DarkWebView';
import AddDarkWebFindingModal from './components/AddDarkWebFindingModal';
import PhishingFeedView from './components/PhishingFeedView';
import AddPhishingURLModal from './components/AddPhishingURLModal';
import BulkUploadPhishingModal from './components/BulkUploadPhishingModal';
import ExecutiveProtectionView from './components/ExecutiveProtectionView';
import AddExecutiveModal from './components/AddExecutiveModal';
import ThreatActorsView from './components/ThreatActorsView';
import AddThreatActorModal from './components/AddThreatActorModal';
import MalwareAnalysisView from './components/MalwareAnalysisView';
import ThreatSearchView from './components/ThreatSearchView';
import VulnerabilityIntelligenceView from './components/VulnerabilityIntelligenceView';
import IOCView from './components/Iocview';
import AssetsView from './components/AssetsView';
import DomainDetectorAdminView from './components/DomainDetectorAdminView';
import AddVulnerabilityModal from './components/AddVulnerabilityModal';
import UpdateVulnerabilityModal from './components/UpdateVulnerabilityModal';  
import BulkUploadVulnerabilityModal from './components/BulkUploadVulnerabilityModal';
import DomainsViewTable from './components/DomainsViewTable';
import BulkUploadDomainsModal from './components/BulkUploadDomainsModal';
import AddExecutiveFindingModal from './components/AddExecutiveFindingModal';
import RansomwareView from './components/RansomwareView';
import CyberNewsView from './components/CyberNewsView';
import CyberNewsWidget from './components/CyberNewsWidget';


import {
  Shield,
  Globe,
  Server,
  Users,
  AlertTriangle,
  Eye,
  Database,
  ChevronRight,
  ChevronDown,
  Bug,
  Calendar,
  User as UserIcon,
  Building2,
  Settings,
  LogOut,
  Filter,
  Download,
  ExternalLink,
  Search,
  CheckCircle,
  XCircle,
  Lock,
  Code,
  FileText,
  Crown,
  RefreshCw,
  Info,
  X,
  Edit,
  Save,
  Newspaper,
  UserPlus,
  Plus,
  Upload
} from 'lucide-react';


// ==============================================
// ThreatMonitoringPlatform
// ==============================================
  const ThreatMonitoringPlatform = () => {
  const [showCompanySelector, setShowCompanySelector] = useState(false);
  const [currentRoute, setCurrentRoute] = useState('/incidents');
  const [adminTab, setAdminTab] = useState('all-users');
  const [settingsTab, setSettingsTab] = useState('company');
  const [showAddImpersonatingDomain, setShowAddImpersonatingDomain] = useState(false);
  const [showAddSocialMedia, setShowAddSocialMedia] = useState(false);
  const [showAddDarkWebFinding, setShowAddDarkWebFinding] = useState(false);
  const [showAddPhishingURL, setShowAddPhishingURL] = useState(false);
  const [showBulkUploadPhishing, setShowBulkUploadPhishing] = useState(false);
const [showAddExecutive, setShowAddExecutive] = useState(false);
const [showAddExecutiveFinding, setShowAddExecutiveFinding] = useState(false);
const [ctiSubmenu, setCtiSubmenu] = useState('/phishing-feed'); // CTI submenu state - defaults to Phishing Feed
const [showCtiDropdown, setShowCtiDropdown] = useState(false); // CTI dropdown visibility state
const [showBulkUpload, setShowBulkUpload] = useState(false);
const [findingRefreshKey, setFindingRefreshKey] = useState(0);

  // CTI dropdown: click-outside-to-close
  const ctiDropdownRef = useRef(null);

  useEffect(() => {
    if (!showCtiDropdown) return;
    const handleClickOutside = (e) => {
      if (ctiDropdownRef.current && !ctiDropdownRef.current.contains(e.target)) {
        setShowCtiDropdown(false);
      }
    };
    // Use 'click' (bubble phase, after React handlers) instead of 'mousedown'
    // so that menu-item onClick handlers fire reliably before this listener evaluates.
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showCtiDropdown]);

  // Clerk & Tenant
  const { user } = useUser();
  const { getToken, isSignedIn } = useAuth();
  const { signOut } = useClerk();
  const { companies, activeCompany, setActiveCompany, loadingCompanies, error } = useTenant();

  // User roles/permissions state
  const [userRole, setUserRole] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loadingUser, setLoadingUser] = useState(true);

  // Fetch user permissions
  useEffect(() => {
    (async () => {
      if (!isSignedIn) return;
      try {
        const token = await getToken();
        const res = await fetch('/api/me', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setUserRole(data.role);
          setIsSuperAdmin(data.is_super_admin === true);
        }
      } catch (e) {
        console.error('Failed to load user info:', e);
      } finally {
        setLoadingUser(false);
      }
    })();
  }, [isSignedIn, getToken]);

  // NEW: Incident types - extensible list
  const incidentTypes = [
    'Domain Impersonation',
    'Impersonating Social Media Account',
    'Suspicious Social Media Content',
    'Employee Credentials Found',
    'Suspicious Dark Web Content',
    'Suspicious Code Repo Detected',
    'Sensitive Repo Content',
    'VIP Credential',
    'VIP Information',
    'Impersonating Domain Change',
    'Cyber Threat Intelligence Update',
    'Phishing',
    'Malware',
    'Credential Theft',
    'Dark Web',
    'Brand Abuse'
  ];

  // NEW: Get icon for incident type
  const getIncidentIcon = (type) => {
    const iconMap = {
      'Domain Impersonation': Globe,
      'Impersonating Social Media Account': Users,
      'Suspicious Social Media Content': Eye,
      'Employee Credentials Found': Lock,
      'Suspicious Dark Web Content': Database,
      'Suspicious Code Repo Detected': Code,
      'Sensitive Repo Content': FileText,
      'VIP Credential': Crown,
      'VIP Information': Crown,
      'Impersonating Domain Change': RefreshCw,
      'Cyber Threat Intelligence Update': Info,
      'Phishing': AlertTriangle,
      'Malware': Shield,
      'Credential Theft': Lock,
      'Dark Web': Database,
      'Brand Abuse': Users
    };
    const Icon = iconMap[type] || Shield;
    return Icon;
  };

  // MODIFIED: Get color for risk/severity level
  const getRiskColor = (risk) => {
    const colors = {
      'Critical': 'bg-red-500/20 text-red-400 border-red-500/30',
      'High': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      'Medium': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      'Low': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'Informational': 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    };
    return colors[risk] || colors['Informational'];
  };

  // NEW: Get color for TLP
  const getTLPColor = (tlp) => {
    const colors = {
      'Red': 'bg-red-600 text-white',
      'Amber+Strict': 'bg-amber-500 text-black',
      'Amber': 'bg-amber-500 text-black',
      'Green': 'bg-green-600 text-white',
      'Clear': 'bg-white text-black'
    };
    return colors[tlp] || colors['Clear'];
  };

  // MODIFIED: Get color for status
  const getStatusColor = (status) => {
    if (status === 'Open') return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    if (status?.includes('Resolved')) return 'bg-green-500/20 text-green-400 border-green-500/30';
    if (status?.includes('Nonissue')) return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    if (status?.includes('Takedown')) return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
    if (status === 'Monitoring') return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30';
    return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  const getPlatformIcon = (platform) => {
    return <Users className="w-4 h-4" />;
  };

  // Dummy data
  const domains = [
    { id: 'DOM-001', domain: 'glareindustriess.com', type: 'Typosquatting', status: 'Active', risk: 'High', registered: '2024-12-15', registrar: 'Namecheap', similarity: '95%', notes: 'Extra "s" at end - likely phishing', ipAddress: '185.234.219.45' },
    { id: 'DOM-002', domain: 'glare-industries.com', type: 'Typosquatting', status: 'Takedown Requested', risk: 'Critical', registered: '2024-11-28', registrar: 'GoDaddy', similarity: '98%', notes: 'Hyphenated version - hosting phishing portal', ipAddress: '192.168.45.23' },
  ];

  const socialMedia = [
    { id: 'SOC-001', platform: 'Facebook', accountName: 'Glare Industries Official', handle: '@glare_industries_official', status: 'Active', risk: 'Critical', followers: '2,847', created: '2024-11-20', verified: false, notes: 'Impersonating official page, posting fake job offers', contentType: 'Phishing/Scam' },
  ];

  const darkWeb = [
    { id: 'DW-001', source: 'RaidForums', title: 'Glare Industries Employee Database', category: 'Data Leak', status: 'Active', risk: 'Critical', discovered: '2024-12-15', recordCount: '12,847 records', dataType: 'Employee PII, Emails, Phone Numbers', notes: 'Database dump claiming to contain employee info', seller: 'DataBroker47', price: '2.5 BTC' },
  ];

  const companyName = activeCompany?.name ?? '-';

  // NEW: Access control flags
  const canSeeAdminSettings = userRole === 'Admin' || isSuperAdmin;
  const canSeeSuperAdminSettings = isSuperAdmin;
  const canEditIncidents = userRole === 'Admin' || isSuperAdmin;

  // NEW: Incidents state
  const [incidents, setIncidents] = useState([]);
  const [loadingIncidents, setLoadingIncidents] = useState(false);
  const [incidentsError, setIncidentsError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isEditingIncident, setIsEditingIncident] = useState(false);
  const [editedIncident, setEditedIncident] = useState(null);

  // NEW: Admin state
  const [allUsers, setAllUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showAddCompany, setShowAddCompany] = useState(false);

  // NEW: Settings state
  const [companyStats, setCompanyStats] = useState(null);
  const [companyUsers, setCompanyUsers] = useState([]);
  
  // NEW: Users for incident assignment dropdown
  const [incidentAssignees, setIncidentAssignees] = useState([]);

  // Fetch incidents
  useEffect(() => {
    (async () => {
      if (!isSignedIn || !activeCompany) return;
      setLoadingIncidents(true);
      setIncidentsError(null);
      try {
        const token = await getToken();
        const r = await fetch(`/api/companies/${activeCompany.id}/incidents`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!r.ok) throw new Error(`Failed to load incidents (${r.status})`);
        const data = await r.json();
        setIncidents(Array.isArray(data) ? data : []);
        if (data.length > 0 && !selectedIncident) {
          setSelectedIncident(data[0]);
        }
      } catch (e) {
        console.error('Incidents load error:', e);
        setIncidentsError(e.message || 'Failed to load incidents');
      } finally {
        setLoadingIncidents(false);
      }
    })();
  }, [isSignedIn, activeCompany, getToken]);

  // NEW: Fetch company users for incident assignment
  useEffect(() => {
    if (!isSignedIn || !activeCompany) return;
    
    const fetchAssignees = async () => {
      try {
        const token = await getToken();
        const res = await fetch(`/api/users?companyId=${activeCompany.numeric_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (res.ok) {
          const users = await res.json();
          // Only include active users
          setIncidentAssignees(users.filter(u => u.active));
        }
      } catch (e) {
        console.error('Failed to load assignees:', e);
      }
    };
    
    fetchAssignees();
  }, [isSignedIn, activeCompany, getToken]);

  // NEW: Fetch all users (Super Admin only)
  useEffect(() => {
    if (!isSuperAdmin || currentRoute !== '/super-admin') return;
    fetchAllUsers();
  }, [isSuperAdmin, currentRoute, getToken]);

  const fetchAllUsers = async () => {
    if (!isSuperAdmin) return;
    setLoadingUsers(true);
    try {
      const token = await getToken();
      const res = await fetch('/api/users', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAllUsers(data);
      }
    } catch (e) {
      console.error('Failed to load users:', e);
    } finally {
      setLoadingUsers(false);
    }
  };

  // NEW: Fetch company stats for Settings
  useEffect(() => {
    if (!canSeeAdminSettings || currentRoute !== '/settings') return;
    if (!activeCompany) return;
    (async () => {
      try {
        const token = await getToken();
        let userCount = 0;
        
        // Fetch company users
        const usersRes = await fetch(`/api/users?companyId=${activeCompany.numeric_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (usersRes.ok) {
          const usersData = await usersRes.json();
          setCompanyUsers(usersData);
          userCount = usersData.length;
        }

        // Fetch incidents count
        const incidentsRes = await fetch(`/api/companies/${activeCompany.id}/incidents`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        let incidentCount = 0;
        if (incidentsRes.ok) {
          const incidentsData = await incidentsRes.json();
          incidentCount = incidentsData.length;
        }

        setCompanyStats({
          ...activeCompany,
          incidentCount,
          userCount
        });
      } catch (e) {
        console.error('Failed to load company stats:', e);
      }
    })();
  }, [canSeeAdminSettings, currentRoute, activeCompany, getToken]);

  // NEW: Filter incidents
  const filteredIncidents = incidents.filter(incident => {
    const matchesSearch = incident.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         incident.incident_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         incident.asset?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || incident.incident_type === filterType;
    const matchesStatus = filterStatus === 'all' || incident.status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  // NEW: Handle incident editing
  const handleEditIncident = () => {
    setEditedIncident({ ...selectedIncident });
    setIsEditingIncident(true);
  };

  const handleCancelEdit = () => {
    setEditedIncident(null);
    setIsEditingIncident(false);
  };

  // NEW: Save incident changes
  const handleSaveIncident = async () => {
    if (!editedIncident || !activeCompany) {
      alert('No incident selected or company context missing.');
      return;
    }

    try {
      const token = await getToken();
      const response = await fetch(
        `/api/companies/${activeCompany.id}/incidents/${editedIncident.incident_id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            incident_type: editedIncident.incident_type,
            title: editedIncident.title,
            asset: editedIncident.asset,
            details: editedIncident.details,
            recommendations: editedIncident.recommendations,
            case_notes: editedIncident.case_notes,
            severity: editedIncident.severity,
            status: editedIncident.status,
            tlp: editedIncident.tlp,
            mitre_ttps: editedIncident.mitre_ttps,
            date: editedIncident.date,
            assigned: editedIncident.assigned
          })
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || error.message || `Failed to update incident (HTTP ${response.status})`);
      }

      const updated = await response.json();

      // Update local state
      setIncidents(incidents.map(inc => 
        inc.incident_id === updated.incident_id ? updated : inc
      ));
      setSelectedIncident(updated);
      setIsEditingIncident(false);
      setEditedIncident(null);
      alert(`Incident "${updated.incident_id}" updated successfully!`);
    } catch (error) {
      console.error('Error saving incident:', error);
      alert(`Error: ${error.message}`);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100 font-sans">
     <CompanyDebugBar onChoose={() => setShowCompanySelector(true)} />

{/* Logo/Branding Section */}
<div className="border-b border-slate-800/50 bg-slate-900/30 backdrop-blur-sm">
  <div className="px-6 py-2.5 flex items-center gap-3">
    <div className="w-8 h-8 rounded-lg flex items-center justify-center">
      <img 
        src="/ftd-portal-1.png" 
        alt="Digital Risk Manager" 
        className="w-8 h-8 object-contain"
      />
    </div>
    <div>
      <h1 className="text-lg font-bold tracking-tight bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
        Digital Risk Manager
      </h1>
      <p className="text-[10px] text-slate-400 tracking-wide uppercase">Threat Intelligence Platform</p>
    </div>
  </div>
</div>

{/* Navigation Menu */}
<div className="border-b border-slate-800/50 bg-slate-900/30 backdrop-blur-sm relative z-40">
  <div className="px-6 py-3 flex items-center justify-between">

          {/* Main Navigation */}
          <div className="flex items-center gap-2">
          <button
  onClick={() => setCurrentRoute('/assets')}
  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
    currentRoute.startsWith('/assets')
      ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
      : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-300'
  }`}
>
  <Server className="w-4 h-4 inline-block mr-2" />
  Assets
</button>

            <button
              onClick={() => setCurrentRoute('/incidents')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                currentRoute === '/incidents'
                  ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                  : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-300'
              }`}
            >
              <AlertTriangle className="w-4 h-4 inline-block mr-2" />
              Incidents
            </button>

            <button
              onClick={() => setCurrentRoute('/domains')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                currentRoute === '/domains' || currentRoute === '/domains-table'
                  ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                  : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-300'
              }`}
            >
              <Globe className="w-4 h-4 inline-block mr-2" />
              Domains
            </button>



            <button
              onClick={() => setCurrentRoute('/social-media')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                currentRoute === '/social-media'
                  ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                  : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-300'
              }`}
            >
              <Users className="w-4 h-4 inline-block mr-2" />
              Social Media
            </button>
            <button
              onClick={() => setCurrentRoute('/dark-web')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                currentRoute === '/dark-web'
                  ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                  : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-300'
              }`}
            >
              <Database className="w-4 h-4 inline-block mr-2" />
              Dark Web
            </button>
<button
  onClick={() => setCurrentRoute('/executive-protection')}
  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
    currentRoute === '/executive-protection'
      ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
      : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-300'
  }`}
>
  <Shield className="w-4 h-4 inline-block mr-2" />
  Executive Protection
</button>


            {/* CTI Dropdown Menu */}
            <div 
              className="relative"
              ref={ctiDropdownRef}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowCtiDropdown(!showCtiDropdown);
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  (currentRoute.startsWith('/cti') || currentRoute === '/phishing-feed') && currentRoute !== '/ransomware'
                    ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                    : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-300'
                }`}
              >
                <Database className="w-4 h-4 inline-block mr-2" />
                Cyber Threat Intelligence
                <ChevronDown className={`w-3 h-3 inline-block ml-1 transition-transform ${showCtiDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Submenu Dropdown */}
              {showCtiDropdown && (
                <div className="absolute left-0 top-full pt-1 w-56 z-[100]">
                  <div className="bg-slate-900 border border-slate-700/60 rounded-lg shadow-2xl shadow-black/50 ring-1 ring-black/20 p-2">
                    <button
                      onClick={() => {
                        setCtiSubmenu('/phishing-feed');
                        setCurrentRoute('/phishing-feed');
                        setShowCtiDropdown(false);
                      }}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                        currentRoute === '/phishing-feed'
                          ? 'bg-cyan-500/20 text-cyan-400'
                          : 'text-slate-300 hover:bg-slate-800/50'
                      }`}
                    >
                      <Shield className="w-4 h-4 inline-block mr-2" />
                      Phishing Feed
                    </button>
                    <button
                      onClick={() => {
                        setCtiSubmenu('/cti/threat-search');
                        setCurrentRoute('/cti/threat-search');
                        setShowCtiDropdown(false);
                      }}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                        currentRoute === '/cti/threat-search'
                          ? 'bg-cyan-500/20 text-cyan-400'
                          : 'text-slate-300 hover:bg-slate-800/50'
                      }`}
                    >
                      <Search className="w-4 h-4 inline-block mr-2" />
                      Threat Search
                    </button>
                    <button
                      onClick={() => {
                        setCtiSubmenu('/cti/threat-actors');
                        setCurrentRoute('/cti/threat-actors');
                        setShowCtiDropdown(false);
                      }}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                        currentRoute === '/cti/threat-actors'
                          ? 'bg-cyan-500/20 text-cyan-400'
                          : 'text-slate-300 hover:bg-slate-800/50'
                      }`}
                    >
                      <Users className="w-4 h-4 inline-block mr-2" />
                      Threat Actors
                    </button>
                    <button
                      onClick={() => {
                        setCtiSubmenu('/cti/malware-analysis');
                        setCurrentRoute('/cti/malware-analysis');
                        setShowCtiDropdown(false);
                      }}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                        currentRoute === '/cti/malware-analysis'
                          ? 'bg-cyan-500/20 text-cyan-400'
                          : 'text-slate-300 hover:bg-slate-800/50'
                      }`}
                    >
                      <Bug className="w-4 h-4 inline-block mr-2" />
                      Malware Analysis
                    </button>
                    <button
                      onClick={() => {
                        setCtiSubmenu('/cti/vulnerability-intelligence');
                        setCurrentRoute('/cti/vulnerability-intelligence');
                        setShowCtiDropdown(false);
                      }}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                        currentRoute === '/cti/vulnerability-intelligence'
                          ? 'bg-cyan-500/20 text-cyan-400'
                          : 'text-slate-300 hover:bg-slate-800/50'
                      }`}
                    >
                      <Shield className="w-4 h-4 inline-block mr-2" />
                      Vulnerability Intelligence
                    </button>
                    <button
                      onClick={() => {
                        setCtiSubmenu('/cti/ioc');
                        setCurrentRoute('/cti/ioc');
                        setShowCtiDropdown(false);
                      }}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                        currentRoute === '/cti/ioc'
                          ? 'bg-cyan-500/20 text-cyan-400'
                          : 'text-slate-300 hover:bg-slate-800/50'
                      }`}
                    >
                      <AlertTriangle className="w-4 h-4 inline-block mr-2" />
                      IOC (Indicators of Compromise)
                    </button>
                    <button
                      onClick={() => {
                        setCtiSubmenu('/cti/cyber-news');
                        setCurrentRoute('/cti/cyber-news');
                        setShowCtiDropdown(false);
                      }}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                        currentRoute === '/cti/cyber-news'
                          ? 'bg-cyan-500/20 text-cyan-400'
                          : 'text-slate-300 hover:bg-slate-800/50'
                      }`}
                    >
                      <Newspaper className="w-4 h-4 inline-block mr-2" />
                      Cybersecurity News
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Ransomware Intelligence - Standalone Route */}
            <button
              onClick={() => setCurrentRoute('/ransomware')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                currentRoute === '/ransomware'
                  ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                  : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-300'
              }`}
            >
              <AlertTriangle className="w-4 h-4 inline-block mr-2" />
              Ransomware Intelligence
            </button>
          </div>

          {/* Right Side Menu - Grouped together */}
<div className="flex items-center gap-3">
  {companies?.length > 1 && (
    <select
      className="px-3 py-1.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm text-slate-200"
      value={activeCompany?.id || ''}
      onChange={(e) => setActiveCompany(companies.find(c => c.id === e.target.value))}
      title="Select company"
    >
      {companies.map(c => (
        <option key={c.id} value={c.id}>{c.name}</option>
      ))}
    </select>
  )}

  {/* Super Admin Settings Button */}
  {canSeeSuperAdminSettings && (
    <button
      onClick={() => setCurrentRoute('/super-admin')}
      className={`p-2 rounded-lg transition-all border ${
        currentRoute === '/super-admin'
          ? 'bg-purple-500/20 text-purple-400 border-purple-500/30'
          : 'border-transparent hover:bg-purple-500/10 hover:border-purple-500/30 text-purple-400'
      }`}
      title="Super Admin"
    >
      <Settings className="w-5 h-5" />
    </button>
  )}

  {/* Admin Settings Button */}
  {canSeeAdminSettings && (
    <button
      onClick={() => setCurrentRoute('/settings')}
      className={`p-2 rounded-lg transition-all border ${
        currentRoute === '/settings'
          ? 'bg-blue-500/20 text-blue-400 border-blue-500/30'
          : 'border-transparent hover:bg-blue-500/10 hover:border-blue-500/30 text-blue-400'
      }`}
      title="Settings"
    >
      <Building2 className="w-5 h-5" />
    </button>
  )}

  {/* User Settings Button */}
  <button
    onClick={() => setCurrentRoute('/user')}
    className={`p-2 rounded-lg transition-all border ${
      currentRoute === '/user'
        ? 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30'
        : 'border-transparent hover:bg-cyan-500/10 hover:border-cyan-500/30 text-cyan-400'
    }`}
    title="User Settings"
  >
    <UserIcon className="w-5 h-5" />
  </button>
</div>
         
        </div>
      </div>

      {showCompanySelector && (
        <CompanySelector onClose={() => setShowCompanySelector(false)} />
      )}
      

      {/* Main Content Area */}
      <div className="flex-1 overflow-hidden relative z-0">
        
        {/* ASSETS ROUTE */}
{currentRoute.startsWith('/assets') && (
  <AssetsView 
    isSuperAdmin={isSuperAdmin}
    canSeeSuperAdminSettings={canSeeSuperAdminSettings}
  />
)}


        {/* INCIDENTS ROUTE - SPLIT VIEW */}
        {currentRoute === '/incidents' && (
          <div className="h-full flex">
            {/* LEFT SIDEBAR */}
            <div className="w-[420px] border-r border-slate-800/50 bg-slate-900/30 flex flex-col">
              <div className="px-4 py-4 border-b border-slate-800/50 bg-gradient-to-r from-slate-900/50 to-slate-900/30">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-bold text-slate-100 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-cyan-400" />
                    Incidents
                  </h2>
                  <button
                    disabled={loadingIncidents || !activeCompany}
                    onClick={() => setShowCreate(true)}
                    className="px-3 py-1.5 bg-cyan-500 hover:bg-cyan-600 disabled:opacity-50 rounded-lg text-xs text-white font-medium transition-all"
                  >
                    + New
                  </button>
                </div>

                <div className="relative mb-3">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search incidents..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20"
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="px-2 py-1.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-xs text-slate-200 focus:outline-none focus:border-cyan-500/50"
                  >
                    <option value="all">All Types</option>
                    {incidentTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-2 py-1.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-xs text-slate-200 focus:outline-none focus:border-cyan-500/50"
                  >
                    <option value="all">All Status</option>
                    <option value="Open">Open</option>
                    <option value="Monitoring">Monitoring</option>
                    <option value="Takedown Requested">Takedown Requested</option>
                    <option value="Resolved">Resolved</option>
                  </select>
                </div>

                <div className="mt-3 text-xs text-slate-400">
                  {filteredIncidents.length} incident{filteredIncidents.length !== 1 ? 's' : ''}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto">
                {loadingCompanies && (
                  <div className="p-4 text-sm text-slate-400">Loading companies...</div>
                )}
                {!loadingCompanies && !activeCompany && (
                  <div className="p-4 text-sm text-slate-400">No company available.</div>
                )}
                {incidentsError && (
                  <div className="m-4 bg-red-500/10 border border-red-500/30 rounded p-3 text-sm text-red-300">
                    {incidentsError}
                  </div>
                )}
                {loadingIncidents && (
                  <div className="p-4 text-sm text-slate-400">Loading incidents...</div>
                )}
                {!loadingIncidents && filteredIncidents.length === 0 && (
                  <div className="p-4 text-sm text-slate-500">No incidents found.</div>
                )}
                {!loadingIncidents && filteredIncidents.map((incident) => {
                  const Icon = getIncidentIcon(incident.incident_type);
                  const isSelected = selectedIncident?.incident_id === incident.incident_id;
                  
                  return (
                    <button
                      key={incident.incident_id}
                      onClick={() => {
                        setSelectedIncident(incident);
                        setIsEditingIncident(false);
                        setEditedIncident(null);
                      }}
                      className={`w-full p-4 border-b border-slate-800/30 text-left transition-all ${
                        isSelected
                          ? 'bg-cyan-500/10 border-l-4 border-l-cyan-500'
                          : 'hover:bg-slate-800/30 border-l-4 border-l-transparent'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4 text-cyan-400" />
                          <span className="text-xs text-slate-400">{incident.incident_type}</span>
                        </div>
                        <span className="text-xs text-slate-500">{incident.date ? new Date(incident.date).toLocaleDateString() : '-'}</span>
                      </div>

                      <div className="text-sm font-medium text-slate-200 mb-2 line-clamp-2">
                        {incident.title}
                      </div>

                      <div className="text-xs text-slate-500 mb-2 font-mono">
                        {incident.incident_id} {incident.asset && `| ${incident.asset}`}
                      </div>

                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getStatusColor(incident.status)}`}>
                          {incident.status}
                        </span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getRiskColor(incident.severity)}`}>
                          {incident.severity}
                        </span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getTLPColor(incident.tlp)}`}>
                          {incident.tlp}
                        </span>
                         {/* MITRE TTPs - show up to 2, then +N more */}
  {incident.mitre_ttps && Array.isArray(incident.mitre_ttps) && incident.mitre_ttps.slice(0, 2).map((ttp) => (
    <span
      key={ttp}
      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-500/20 border border-purple-500/30 text-purple-300"
    >
      {ttp}
    </span>
  ))}
  {incident.mitre_ttps && incident.mitre_ttps.length > 2 && (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs text-slate-500">
      +{incident.mitre_ttps.length - 2}
    </span>
  )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* RIGHT PANEL - Incident Details */}
            <div className="flex-1 overflow-y-auto">
              {selectedIncident ? (
                <div className="p-8">
                  <div className="mb-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          {React.createElement(getIncidentIcon(selectedIncident.incident_type), {
                            className: "w-6 h-6 text-cyan-400"
                          })}
                          {isEditingIncident ? (
                            <input
                              type="text"
                              value={editedIncident.title}
                              onChange={(e) => setEditedIncident({ ...editedIncident, title: e.target.value })}
                              className="text-2xl font-bold bg-slate-800/50 border border-slate-700/50 rounded px-3 py-1 text-slate-100 flex-1"
                            />
                          ) : (
                            <h2 className="text-2xl font-bold text-slate-100">
                              {selectedIncident.title}
                            </h2>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-sm text-slate-400">
                          <span className="font-mono">{selectedIncident.incident_id}</span>
                          <span>|</span>
                          {isEditingIncident ? (
                            <select
                              value={editedIncident.incident_type}
                              onChange={(e) => setEditedIncident({ ...editedIncident, incident_type: e.target.value })}
                              className="bg-slate-800/50 border border-slate-700/50 rounded px-2 py-1 text-slate-200 text-sm"
                            >
                              {incidentTypes.map(type => (
                                <option key={type} value={type}>{type}</option>
                              ))}
                            </select>
                          ) : (
                            <span>{selectedIncident.incident_type}</span>
                          )}
                          <span>|</span>
                          {isEditingIncident ? (
                            <input
                              type="date"
                              value={editedIncident.date}
                              onChange={(e) => setEditedIncident({ ...editedIncident, date: e.target.value })}
                              className="bg-slate-800/50 border border-slate-700/50 rounded px-2 py-1 text-slate-200 text-sm"
                            />
                          ) : (
                            <span>{selectedIncident.date ? new Date(selectedIncident.date).toLocaleDateString() : '-'}</span>
                          )}
                        </div>
                      </div>
                      {!isEditingIncident && (
                        <button
                          onClick={() => setSelectedIncident(null)}
                          className="p-2 hover:bg-slate-800/50 rounded-lg transition-all"
                        >
                          <X className="w-5 h-5 text-slate-400" />
                        </button>
                      )}
                    </div>

                    {/* Status Badges */}
                    <div className="flex items-center gap-2">
                      {isEditingIncident ? (
                        <>
                          <select
                            value={editedIncident.status}
                            onChange={(e) => setEditedIncident({ ...editedIncident, status: e.target.value })}
                            className="px-3 py-1 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm text-slate-200"
                          >
                            <option value="Open">Open</option>
                            <option value="Monitoring">Monitoring</option>
                            <option value="Takedown Requested">Takedown Requested</option>
                            <option value="Resolved">Resolved</option>
                            <option value="Resolved - Nonissue">Resolved - Nonissue</option>
                          </select>
                          <select
                            value={editedIncident.severity}
                            onChange={(e) => setEditedIncident({ ...editedIncident, severity: e.target.value })}
                            className="px-3 py-1 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm text-slate-200"
                          >
                            <option value="Critical">Critical</option>
                            <option value="High">High</option>
                            <option value="Medium">Medium</option>
                            <option value="Low">Low</option>
                            <option value="Informational">Informational</option>
                          </select>
                          <select
                            value={editedIncident.tlp}
                            onChange={(e) => setEditedIncident({ ...editedIncident, tlp: e.target.value })}
                            className="px-3 py-1 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm text-slate-200"
                          >
                            <option value="Red">Red</option>
                            <option value="Amber">Amber</option>
                            <option value="Green">Green</option>
                            <option value="Clear">Clear</option>
                          </select>
                               {/* MITRE TTPs Editor - comma-separated */}
      <input
        type="text"
        value={Array.isArray(editedIncident.mitre_ttps) ? editedIncident.mitre_ttps.join(', ') : ''}
        onChange={(e) => {
          const ttps = e.target.value
            .split(',')
            .map(t => t.trim().toUpperCase())
            .filter(t => t.length > 0);
          setEditedIncident({ ...editedIncident, mitre_ttps: ttps });
        }}
        placeholder="TTPs: T1566, T1059"
        className="px-3 py-1 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm text-slate-200"
      />
    </>
  ) : (
    <>
      <span className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium border ${getStatusColor(selectedIncident.status)}`}>
        {selectedIncident.status}
      </span>
      <span className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium border ${getRiskColor(selectedIncident.severity)}`}>
        {selectedIncident.severity} Risk
      </span>
      <span className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium ${getTLPColor(selectedIncident.tlp)}`}>
        TLP:{selectedIncident.tlp}
      </span>
      
   {/* MITRE TTPs - all badges, clickable to MITRE site */}
{Array.isArray(selectedIncident?.mitre_ttps) &&
  selectedIncident.mitre_ttps.map((ttp) => (
    <a
      key={ttp}
      href={`https://attack.mitre.org/techniques/${String(ttp).replace('.', '/')}/`}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 px-3 py-1 rounded-lg text-sm font-medium bg-purple-500/20 border border-purple-500/30 text-purple-300 hover:bg-purple-500/30 transition-all"
      title={`View ${ttp} on MITRE ATT&CK`}
    >
      {ttp}
      <ExternalLink className="w-3 h-3" />
    </a>
  ))}
    </>
  )}
</div>
                    
                    </div>
                 

                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-6">
                      <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4">
                        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                          Asset
                        </h3>
                        {isEditingIncident ? (
                          <input
                            type="text"
                            value={editedIncident.asset || ''}
                            onChange={(e) => setEditedIncident({ ...editedIncident, asset: e.target.value })}
                            className="w-full bg-slate-900/50 border border-slate-700/50 rounded px-3 py-2 text-slate-200"
                            placeholder="domain.com / handle / system name"
                          />
                        ) : (
                          <p className="text-slate-200">{selectedIncident.asset || '-'}</p>
                        )}
                      </div>
                      <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4">
                        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                          Assigned To
                        </h3>
                        {isEditingIncident ? (
                          <select
                            value={editedIncident.assigned || ''}
                            onChange={(e) => setEditedIncident({ ...editedIncident, assigned: e.target.value })}
                            className="w-full bg-slate-900/50 border border-slate-700/50 rounded px-3 py-2 text-slate-200"
                          >
                            <option value="">Unassigned</option>
                            {incidentAssignees.map((user) => (
                              <option key={user.id} value={user.email}>
                                {user.first_name && user.last_name 
                                  ? `${user.first_name} ${user.last_name} (${user.email})`
                                  : user.email
                                }
                              </option>
                            ))}
                          </select>
                        ) : (
                          <p className="text-slate-200">{selectedIncident.assigned || 'Unassigned'}</p>
                        )}
                      </div>
                    </div>

                    {(selectedIncident.details || isEditingIncident) && (
                      <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4">
                        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                          Details
                        </h3>
                        {isEditingIncident ? (
                          <textarea
                            rows={6}
                            value={editedIncident.details || ''}
                            onChange={(e) => setEditedIncident({ ...editedIncident, details: e.target.value })}
                            className="w-full bg-slate-900/50 border border-slate-700/50 rounded px-3 py-2 text-slate-200"
                            placeholder="What happened?"
                          />
                        ) : (
                          <p className="text-slate-200 whitespace-pre-wrap">{selectedIncident.details}</p>
                        )}
                      </div>
                    )}

                    {(selectedIncident.recommendations || isEditingIncident) && (
                      <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4">
                        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                          Recommendations
                        </h3>
                        {isEditingIncident ? (
                          <textarea
                            rows={4}
                            value={editedIncident.recommendations || ''}
                            onChange={(e) => setEditedIncident({ ...editedIncident, recommendations: e.target.value })}
                            className="w-full bg-slate-900/50 border border-slate-700/50 rounded px-3 py-2 text-slate-200"
                            placeholder="Next steps / actions"
                          />
                        ) : (
                          <p className="text-slate-200 whitespace-pre-wrap">{selectedIncident.recommendations}</p>
                        )}
                      </div>
                    )}

                    {(selectedIncident.case_notes || isEditingIncident) && (
                      <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4">
                        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                          Case Notes
                        </h3>
                        {isEditingIncident ? (
                          <textarea
                            rows={3}
                            value={editedIncident.case_notes || ''}
                            onChange={(e) => setEditedIncident({ ...editedIncident, case_notes: e.target.value })}
                            className="w-full bg-slate-900/50 border border-slate-700/50 rounded px-3 py-2 text-slate-200"
                            placeholder="Internal notes"
                          />
                        ) : (
                          <p className="text-slate-200 whitespace-pre-wrap">{selectedIncident.case_notes}</p>
                        )}
                      </div>
                    )}

                    <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4">
                      <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                        Metadata
                      </h3>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-slate-500">Created:</span>
                          <span className="ml-2 text-slate-200">
                            {selectedIncident.created_at ? new Date(selectedIncident.created_at).toLocaleDateString() : '-'}
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-500">Created By:</span>
                          <span className="ml-2 text-slate-200">{selectedIncident.created_by || '-'}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Updated:</span>
                          <span className="ml-2 text-slate-200">
                            {selectedIncident.updated_at ? new Date(selectedIncident.updated_at).toLocaleDateString() : '-'}
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-500">Updated By:</span>
                          <span className="ml-2 text-slate-200">{selectedIncident.updated_by || '-'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    {canEditIncidents && (
                      <div className="flex gap-3">
                        {isEditingIncident ? (
                          <>
                            <button
                              onClick={handleSaveIncident}
                              className="px-4 py-2 bg-green-500 hover:bg-green-600 rounded-lg text-sm text-white transition-all flex items-center gap-2"
                            >
                              <Save className="w-4 h-4" />
                              Save Changes
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm text-slate-200 transition-all"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={handleEditIncident}
                            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm text-slate-200 transition-all flex items-center gap-2"
                          >
                            <Edit className="w-4 h-4" />
                            Edit Incident
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center">
                    <AlertTriangle className="w-16 h-16 text-slate-700 mx-auto mb-4" />
                    <p className="text-slate-400">Select an incident to view details</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* SUPER ADMIN ROUTE */}
        {currentRoute === '/super-admin' && canSeeSuperAdminSettings && (
          <div className="h-full flex flex-col">
            <div className="px-8 py-6 border-b border-slate-800/50 bg-gradient-to-r from-purple-900/30 to-slate-900/30">
              <h2 className="text-2xl font-bold text-slate-100 flex items-center gap-3">
                <Settings className="w-7 h-7 text-purple-400" />
                Super Admin Settings
              </h2>
              <p className="text-sm text-slate-400 mt-1">System-wide administration</p>
            </div>

            <div className="flex-1 flex">
              {/* Left Menu */}
              <div className="w-64 border-r border-slate-800/50 bg-slate-900/30 p-4">
                <nav className="space-y-2">
                  <button
                    onClick={() => setAdminTab('all-users')}
                    className={`w-full text-left px-4 py-2 rounded-lg text-sm transition-all ${
                      adminTab === 'all-users'
                        ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                        : 'text-slate-400 hover:bg-slate-800/50'
                    }`}
                  >
                    <Users className="w-4 h-4 inline-block mr-2" />
                    All Users
                  </button>
                  <button
                    onClick={() => setAdminTab('add-user')}
                    className={`w-full text-left px-4 py-2 rounded-lg text-sm transition-all ${
                      adminTab === 'add-user'
                        ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                        : 'text-slate-400 hover:bg-slate-800/50'
                    }`}
                  >
                    <UserPlus className="w-4 h-4 inline-block mr-2" />
                    Add User
                  </button>
                  <button
                    onClick={() => setAdminTab('add-incident')}
                    className={`w-full text-left px-4 py-2 rounded-lg text-sm transition-all ${
                      adminTab === 'add-incident'
                        ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                        : 'text-slate-400 hover:bg-slate-800/50'
                    }`}
                  >
                    <Plus className="w-4 h-4 inline-block mr-2" />
                    Add Incident
                  </button>
                  <button
                    onClick={() => setAdminTab('add-company')}
                    className={`w-full text-left px-4 py-2 rounded-lg text-sm transition-all ${
                      adminTab === 'add-company'
                        ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                        : 'text-slate-400 hover:bg-slate-800/50'
                    }`}
                  >
                    <Building2 className="w-4 h-4 inline-block mr-2" />
                    Add Company
                  </button>
                  <button
  onClick={() => setAdminTab('add-impersonating-domain')}
  className={`w-full text-left px-4 py-2 rounded-lg text-sm transition-all ${
    adminTab === 'add-impersonating-domain'
      ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
      : 'text-slate-400 hover:bg-slate-800/50'
  }`}
>
  <Globe className="w-4 h-4 inline-block mr-2" />
  Add Impersonating Domain
</button>

<button
  onClick={() => setAdminTab('add-dark-web')}
  className={`w-full text-left px-4 py-2 rounded-lg text-sm transition-all ${
    adminTab === 'add-dark-web'
      ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
      : 'text-slate-400 hover:bg-slate-800/50'
  }`}
>
  <Database className="w-4 h-4 inline-block mr-2" />
  Add Dark Web Finding
</button>

<button
  onClick={() => setAdminTab('domain-detector')}
  className={`w-full text-left px-4 py-2 rounded-lg text-sm transition-all ${
    adminTab === 'domain-detector'
      ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
      : 'text-slate-400 hover:bg-slate-800/50'
  }`}
>
  <Globe className="w-4 h-4 inline-block mr-2" />
  Domain Detector
</button>
             </nav>
              </div>

              {/* Right Content */}
              <div className="flex-1 overflow-y-auto p-8">
                {adminTab === 'all-users' && (
                  <div>
                    <h3 className="text-xl font-bold text-slate-100 mb-4">All Users</h3>
                    {loadingUsers ? (
                      <div className="text-slate-400">Loading users...</div>
                    ) : (
                      <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 overflow-hidden">
                        <table className="w-full">
                          <thead className="bg-slate-800/50 border-b border-slate-700/50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Email</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Name</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Role</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Company</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-700/30">
                            {allUsers.map((u) => (
                              <tr key={u.id} className="hover:bg-slate-800/30">
                                <td className="px-4 py-3 text-sm text-slate-200">{u.email}</td>
                                <td className="px-4 py-3 text-sm text-slate-300">{u.first_name} {u.last_name}</td>
                                <td className="px-4 py-3 text-sm text-slate-300">{u.role}</td>
                                <td className="px-4 py-3 text-sm text-slate-400">{u.company_name || `ID: ${u.company_numeric_id}`}</td>
                                <td className="px-4 py-3 text-sm">
                                  <span className={`px-2 py-1 rounded text-xs ${u.active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                                    {u.active ? 'Active' : 'Inactive'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {adminTab === 'add-user' && (
                  <div>
                    <AddUserForm 
                      onUserCreated={() => {
                        fetchAllUsers();
                        setAdminTab('all-users');
                      }}
                    />
                  </div>
                )}

                {adminTab === 'add-incident' && (
                  <div>
                    <h3 className="text-xl font-bold text-slate-100 mb-4">Add Incident</h3>
                    <button
                      onClick={() => setShowCreate(true)}
                      className="px-4 py-2 bg-purple-500 hover:bg-purple-600 rounded-lg text-sm text-white transition-all"
                    >
                      Open Incident Form
                    </button>
                  </div>
                )}

                {adminTab === 'add-company' && (
                  <div>
                    <h3 className="text-xl font-bold text-slate-100 mb-4">Add Company</h3>
                    <button
                      onClick={() => setShowAddCompany(true)}
                      className="px-4 py-2 bg-purple-500 hover:bg-purple-600 rounded-lg text-sm text-white transition-all"
                    >
                      Open Company Form
                    </button>
                  </div>
                )}
                {adminTab === 'add-impersonating-domain' && (
  <div>
    <h3 className="text-xl font-bold text-slate-100 mb-4">Add Impersonating Domain</h3>
    <div className="flex gap-3">
      <button
        onClick={() => setShowAddImpersonatingDomain(true)}
        className="px-4 py-2 bg-purple-500 hover:bg-purple-600 rounded-lg text-sm text-white transition-all"
      >
        Open Domain Form
      </button>
      <button
        onClick={() => setShowBulkUpload(true)}
        className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 rounded-lg text-sm text-white transition-all flex items-center gap-2"
      >
        <Upload className="w-4 h-4" />
        Bulk Upload CSV
      </button>
    </div>
  </div>
)}

{adminTab === 'add-dark-web' && (
  <div>
    <h3 className="text-xl font-bold text-slate-100 mb-4">Add Dark Web Finding</h3>
    <button
      onClick={() => setShowAddDarkWebFinding(true)}
      className="px-4 py-2 bg-purple-500 hover:bg-purple-600 rounded-lg text-sm text-white transition-all"
    >
      Open Dark Web Finding Form
    </button>
  </div>
)}



{adminTab === 'domain-detector' && (
  <div>
    <DomainDetectorAdminView />
  </div>
)}




              </div>
            </div>
          </div>
        )}

        {/* ADMIN SETTINGS ROUTE */}
        {currentRoute === '/settings' && canSeeAdminSettings && (
          <div className="h-full flex flex-col">
            <div className="px-8 py-6 border-b border-slate-800/50 bg-gradient-to-r from-blue-900/30 to-slate-900/30">
              <h2 className="text-2xl font-bold text-slate-100 flex items-center gap-3">
                <Building2 className="w-7 h-7 text-blue-400" />
                Company Settings
              </h2>
              <p className="text-sm text-slate-400 mt-1">{companyName}</p>
            </div>

            <div className="flex-1 overflow-y-auto p-8">
              {companyStats ? (
                <div className="space-y-6">
                  {/* Company Info */}
                  <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-6">
                    <h3 className="text-lg font-semibold text-slate-100 mb-4">Company Information</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-slate-500">Company Name:</span>
                        <span className="ml-2 text-slate-200">{companyStats.name}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Industry:</span>
                        <span className="ml-2 text-slate-200">{companyStats.industry || '-'}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Subscription Plan:</span>
                        <span className="ml-2 text-slate-200">{companyStats.subscription_tier}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Max Users:</span>
                        <span className="ml-2 text-slate-200">{companyStats.max_users}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Max Incidents:</span>
                        <span className="ml-2 text-slate-200">{companyStats.max_incidents?.toLocaleString()}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Current Incidents:</span>
                        <span className="ml-2 text-slate-200">{companyStats.incidentCount}</span>
                      </div>
                    </div>
                  </div>

                  {/* Users */}
                  <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-6">
                    <h3 className="text-lg font-semibold text-slate-100 mb-4">
                      Users ({companyUsers.length}/{companyStats.max_users})
                    </h3>
                    <div className="space-y-2">
                      {companyUsers.map((u) => (
                        <div key={u.id} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                          <div>
                            <div className="text-sm font-medium text-slate-200">{u.first_name} {u.last_name}</div>
                            <div className="text-xs text-slate-400">{u.email}</div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-slate-400">{u.role}</span>
                            <span className={`px-2 py-1 rounded text-xs ${u.active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                              {u.active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-slate-400">Loading company information...</div>
              )}
            </div>
          </div>
        )}

        {/* USER SETTINGS ROUTE */}
        {currentRoute === '/user' && (
          <div className="h-full flex flex-col">
            <div className="px-8 py-6 border-b border-slate-800/50 bg-gradient-to-r from-cyan-900/30 to-slate-900/30">
              <h2 className="text-2xl font-bold text-slate-100 flex items-center gap-3">
                <UserIcon className="w-7 h-7 text-cyan-400" />
                User Profile
              </h2>
            </div>

            <div className="flex-1 overflow-y-auto p-8">
              <div className="max-w-2xl">
                <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-6 mb-6">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center">
                      <span className="text-2xl font-bold text-white">
                        {(user?.firstName?.[0] || 'U').toUpperCase()}
                        {(user?.lastName?.[0] || 'N').toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-100">
                        {user?.firstName} {user?.lastName}
                      </h3>
                      <p className="text-slate-400">{user?.emailAddresses?.[0]?.emailAddress}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm mb-6">
                    <div>
                      <span className="text-slate-500">Role:</span>
                      <span className="ml-2 text-slate-200">{userRole || 'Loading...'}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Company:</span>
                      <span className="ml-2 text-slate-200">{companyName}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Super Admin:</span>
                      <span className="ml-2 text-slate-200">{isSuperAdmin ? 'Yes' : 'No'}</span>
                    </div>
                  </div>

                  <button
                    onClick={() => signOut({ redirectUrl: '/' })}
                    className="w-full px-4 py-3 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 rounded-lg text-sm text-red-400 transition-all flex items-center justify-center gap-2"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
{/* DOMAINS ROUTE - TABLE INTERFACE (canonical) */}
{(currentRoute === '/domains' || currentRoute === '/domains-table') && (
  <DomainsViewTable
    canSeeSuperAdminSettings={canSeeSuperAdminSettings}
    canEditIncidents={canEditIncidents}
    setShowAddImpersonatingDomain={setShowAddImpersonatingDomain}
  />
)}

{/* SOCIAL MEDIA ROUTE - FULL INTERFACE */}
{currentRoute === '/social-media' && (
  <SocialMediaView
    canSeeSuperAdminSettings={canSeeSuperAdminSettings}
    canEditIncidents={canEditIncidents}
    setShowAddSocialMedia={setShowAddSocialMedia}
  />
)}
{/* PHISHING FEED ROUTE - GLOBAL FEED */}
{currentRoute === '/phishing-feed' && (
  <PhishingFeedView
    isSuperAdmin={isSuperAdmin}
    setShowAddPhishingURL={setShowAddPhishingURL}
    setShowBulkUploadPhishing={setShowBulkUploadPhishing}
  />
)}
{/* DARK WEB ROUTE - FULL INTERFACE */}
{currentRoute === '/dark-web' && (
  <DarkWebView
    canSeeSuperAdminSettings={canSeeSuperAdminSettings}
    canEditIncidents={canEditIncidents}
    setShowAddDarkWebFinding={setShowAddDarkWebFinding}
  />
)}

{/* EXECUTIVE PROTECTION ROUTE */}
{currentRoute === '/executive-protection' && (
  <ExecutiveProtectionView
    canSeeSuperAdminSettings={canSeeSuperAdminSettings}
    canEditIncidents={canEditIncidents}
    setShowAddExecutive={setShowAddExecutive}
    setShowAddExecutiveFinding={setShowAddExecutiveFinding}
    findingRefreshKey={findingRefreshKey}
  />
)}

{/* CYBER THREAT INTELLIGENCE ROUTES */}
{currentRoute === '/cti/threat-search' && (
  <ThreatSearchView />
)}

{currentRoute === '/cti/threat-actors' && (
  <ThreatActorsView
    isSuperAdmin={isSuperAdmin}
    canEditIncidents={canEditIncidents}
  />
)}

{currentRoute === '/cti/malware-analysis' && (
  <MalwareAnalysisView
    isSuperAdmin={isSuperAdmin}
    canEditIncidents={canEditIncidents}
  />
)}

{currentRoute === '/cti/vulnerability-intelligence' && (
  <VulnerabilityIntelligenceView
    isSuperAdmin={isSuperAdmin}
    canEditIncidents={canEditIncidents}
  />
)}

{currentRoute === '/cti/ioc' && (
  <IOCView
    isSuperAdmin={isSuperAdmin}
    canEditIncidents={canEditIncidents}
  />
)}

{currentRoute === '/ransomware' && (
  <RansomwareView isSuperAdmin={isSuperAdmin} />
)}

{currentRoute === '/cti/cyber-news' && (
  <CyberNewsView
    isSuperAdmin={isSuperAdmin}
  />
)}
        {/* Keep all other existing routes: domains, social-media, dark-web unchanged */}
      </div>

      {/* Modals */}
      <CreateIncidentModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(created) => {
          setIncidents((prev) => [created, ...prev]);
          setSelectedIncident(created);
        }}
      />

      {showAddCompany && (
        <AddCompanyModal
          isOpen={showAddCompany}
          onClose={() => setShowAddCompany(false)}
          onCompanyAdded={(newCompany) => {
            console.log('Company added:', newCompany);
            setShowAddCompany(false);
          }}
        />
      )}
      
      {showAddImpersonatingDomain && (
  <AddImpersonatingDomainModal
    isOpen={showAddImpersonatingDomain}
    onClose={() => setShowAddImpersonatingDomain(false)}
    onDomainAdded={(newDomain) => {
      console.log('Impersonating domain added:', newDomain);
      setShowAddImpersonatingDomain(false);
    }}
    companies={companies}
  />
)}

{/* ADD THESE NEW PHISHING FEED MODALS */}
{showAddPhishingURL && (
  <AddPhishingURLModal
    isOpen={showAddPhishingURL}
    onClose={() => setShowAddPhishingURL(false)}
    onURLAdded={(newURL) => {
      console.log('Phishing URL added:', newURL);
      setShowAddPhishingURL(false);
      // Optionally refresh the phishing feed view
      if (currentRoute === '/phishing-feed') {
        window.location.reload(); // Simple refresh, or implement state update
      }
    }}
  />
)}

{showBulkUploadPhishing && (
  <BulkUploadPhishingModal
    isOpen={showBulkUploadPhishing}
    onClose={() => setShowBulkUploadPhishing(false)}
    onUploadComplete={() => {
      console.log('Bulk upload completed');
      setShowBulkUploadPhishing(false);
      // Optionally refresh the phishing feed view
      if (currentRoute === '/phishing-feed') {
        window.location.reload(); // Simple refresh, or implement state update
      }
    }}
  />
)}


      {showAddSocialMedia && (
        <AddSocialMediaModal
          isOpen={showAddSocialMedia}
          onClose={() => setShowAddSocialMedia(false)}
          onAccountAdded={(newAccount) => {
            console.log('Social media account added:', newAccount);
            setShowAddSocialMedia(false);
          }}
          companies={companies}
        />
      )}
      
      {showAddDarkWebFinding && (
  <AddDarkWebFindingModal
    isOpen={showAddDarkWebFinding}
    onClose={() => setShowAddDarkWebFinding(false)}
    onFindingAdded={(newFinding) => {
      console.log('Dark web finding added:', newFinding);
      setShowAddDarkWebFinding(false);
    }}
    companies={companies}
  />
)}
{showAddExecutive && (
  <AddExecutiveModal
    isOpen={showAddExecutive}
    onClose={() => setShowAddExecutive(false)}
    onExecutiveAdded={(newExec) => {
      console.log('Executive added:', newExec);
      setShowAddExecutive(false);
      // Refresh the view if needed
      if (currentRoute === '/executive-protection') {
        window.location.reload();
      }
    }}
    companies={companies}
  />
)}


<AddExecutiveFindingModal
  isOpen={showAddExecutiveFinding}
  onClose={() => setShowAddExecutiveFinding(false)}
  onFindingAdded={() => {
    setShowAddExecutiveFinding(false);
    setFindingRefreshKey(prev => prev + 1);
  }}
/>
{showBulkUpload && (
  <BulkUploadDomainsModal
    isOpen={showBulkUpload}
    onClose={() => setShowBulkUpload(false)}
    onUploadComplete={() => {
      console.log('Bulk domain upload completed');
      setShowBulkUpload(false);
      if (currentRoute === '/domains' || currentRoute === '/domains-table') {
        window.location.reload();
      }
    }}
    activeCompany={activeCompany}
  />
)}
    </div>
  );
};

export default ThreatMonitoringPlatform;